import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { JsonValueEditorComponent } from './json-value-editor.component';

@Component({
  selector: 'app-org-data-json-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatExpansionModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    JsonValueEditorComponent,
  ],
  templateUrl: './org-data-json-editor.component.html',
  styleUrl: './org-data-json-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrgDataJsonEditorComponent {
  @Input({ required: true }) data: Record<string, unknown> = {};
  @Input() excludedKeys: string[] = [];
  @Input() excludedHint = '';

  @Output() dataChange = new EventEmitter<Record<string, unknown>>();

  newTopLevelKey = '';

  get topLevelKeys(): string[] {
    return Object.keys(this.data)
      .filter((k) => !this.excludedKeys.includes(k))
      .sort((a, b) => a.localeCompare(b, 'da'));
  }

  valueForKey(key: string): unknown {
    return this.data[key];
  }

  onKeyValueChange(key: string, value: unknown): void {
    const next = { ...this.data, [key]: value };
    this.dataChange.emit(next);
  }

  removeKey(key: string): void {
    const next = { ...this.data };
    delete next[key];
    this.dataChange.emit(next);
  }

  addTopLevelKey(): void {
    const key = this.newTopLevelKey.trim();
    if (!key || this.excludedKeys.includes(key)) {
      return;
    }
    if (Object.prototype.hasOwnProperty.call(this.data, key)) {
      return;
    }
    this.newTopLevelKey = '';
    this.dataChange.emit({ ...this.data, [key]: '' });
  }

  typeLabel(value: unknown): string {
    if (value === null) {
      return 'null';
    }
    if (Array.isArray(value)) {
      return `Liste (${value.length})`;
    }
    if (typeof value === 'object') {
      const n = Object.keys(value as Record<string, unknown>).length;
      return `Objekt (${n} felter)`;
    }
    if (typeof value === 'boolean') {
      return value ? 'sand' : 'falsk';
    }
    if (typeof value === 'number') {
      return 'tal';
    }
    if (typeof value === 'string') {
      const s = value;
      return s.length > 36 ? `tekst: ${s.slice(0, 36)}…` : `tekst: ${s}`;
    }
    return String(typeof value);
  }
}
